import React, { useMemo, useState, useEffect } from 'react';
import {
  Card,
  List,
  Tag,
  Typography,
  Button,
  Space,
  Spin,
  Empty,
  Divider,
  message,
  Select,
  Checkbox,
  Alert,
  Collapse,
} from 'antd';
import {
  BellOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CheckOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useDashboardAlerts, type Alert as DashboardAlert } from '@/hooks/useManufacturingDashboard';
import api from '@/services/api.service';
import { taskModuleLabel } from '@/services/unifiedTask.service';
import pushNotificationService from '@/services/pushNotification.service';
import { BrowserPushManager } from '@/components/notifications/BrowserPushManager';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Paragraph, Text } = Typography;

const MODULE_FILTER_OPTIONS = [
  { value: '', label: 'All modules' },
  { value: 'Approval', label: 'Approval' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Sales', label: 'Growth & Revenue' },
  { value: 'Procurement', label: 'Procurement' },
  { value: 'CRM', label: 'CRM' },
];

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'Approval', label: 'Approval' },
  { value: 'Alert', label: 'Alert' },
  { value: 'DueDate', label: 'Due dates' },
];

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

interface InAppNotificationRow {
  id: string;
  notification_title: string;
  notification_message: string;
  notification_type: string;
  priority?: string;
  is_read: boolean;
  created_at: string;
  module?: string;
  category?: string;
  action_url?: string | null;
  metadata?: string | null;
}

function parseEventCode(metadata?: string | null): string | null {
  if (!metadata) return null;
  try {
    const obj = JSON.parse(metadata) as { event_code?: string };
    return typeof obj.event_code === 'string' ? obj.event_code : null;
  } catch {
    return null;
  }
}

function formatEventCode(code: string): string {
  return code.replace(/^approval\./, '').replace(/_/g, ' ');
}

const severityToTag = (severity: DashboardAlert['severity']) => {
  if (severity === 'critical') return { color: 'error' as const, label: 'CRITICAL' };
  if (severity === 'warning') return { color: 'warning' as const, label: 'WARNING' };
  return { color: 'default' as const, label: 'INFO' };
};

const LiveAlertIcon: React.FC<{ severity: DashboardAlert['severity'] }> = ({ severity }) => {
  if (severity === 'critical') return <WarningOutlined style={{ color: '#ff4d4f' }} />;
  if (severity === 'warning') return <WarningOutlined style={{ color: '#faad14' }} />;
  return <InfoCircleOutlined style={{ color: '#145A54' }} />;
};

const InboxIcon: React.FC<{ type: string }> = ({ type }) => {
  const t = (type || '').toLowerCase();
  if (t.includes('warn')) return <WarningOutlined style={{ color: '#faad14' }} />;
  if (t.includes('alert') || t.includes('error')) return <WarningOutlined style={{ color: '#ff4d4f' }} />;
  if (t.includes('success')) return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  return <InfoCircleOutlined style={{ color: '#145A54' }} />;
};

const MyAlertsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filterUnread, setFilterUnread] = useState(false);
  const [filterModule, setFilterModule] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data: alerts, isLoading: liveLoading, isError: liveError, refetch: refetchLive } =
    useDashboardAlerts(60000);

  const inboxQueryKey = useMemo(
    () => ['notifications', 'inbox', filterUnread, filterModule, filterCategory] as const,
    [filterUnread, filterModule, filterCategory]
  );

  const {
    data: inboxRows = [],
    isLoading: inboxLoading,
    isError: inboxError,
    error: inboxErrorObj,
    refetch: refetchInbox,
  } = useQuery({
    queryKey: inboxQueryKey,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '200' });
      if (filterUnread) params.set('unread_only', '1');
      if (filterModule) params.set('module', filterModule);
      if (filterCategory) params.set('category', filterCategory);
      const res = await api.get(`/notifications?${params.toString()}`);
      const list = unwrap<InAppNotificationRow[]>(res);
      return Array.isArray(list) ? list : [];
    },
  });

  useEffect(() => {
    setSelectedIds([]);
  }, [filterUnread, filterModule, filterCategory]);

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/notifications/sync-dashboard');
      return unwrap<{ inserted: number }>(res);
    },
    onSuccess: async (data) => {
      message.success(`Synced: ${data?.inserted ?? 0} new notification(s)`);
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (e: Error) => {
      message.error(e?.message || 'Sync failed');
    },
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/notifications/${id}/read`);
    },
    onSuccess: () => {
      message.success('Marked as read');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const bulkReadMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await api.post('/notifications/mark-read-bulk', { ids });
      return unwrap<{ updated?: number }>(res);
    },
    onSuccess: (data) => {
      message.success(`Marked ${data?.updated ?? 0} as read`);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (e: Error) => {
      message.error(e?.message || 'Bulk update failed');
    },
  });

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const unreadSelectable = inboxRows.filter((r) => !r.is_read).map((r) => r.id);
  const allSelected =
    unreadSelectable.length > 0 && unreadSelectable.every((id) => selectedIds.includes(id));

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? [...unreadSelectable] : []);
  };

  const openNotification = async (item: InAppNotificationRow) => {
    const target = item.action_url || '/home/approvals';
    try {
      const safeUrl = await pushNotificationService.openNotificationUrl(target, item.id);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      navigate(safeUrl);
    } catch {
      message.error('Could not open notification');
    }
  };

  const liveList = useMemo(() => {
    if (!alerts) return [];
    const combined: DashboardAlert[] = [
      ...(alerts.critical || []),
      ...(alerts.warnings || []),
      ...(alerts.info || []),
    ];
    return combined;
  }, [alerts]);

  return (
    <div>
      <Title level={2}>
        <BellOutlined /> My Alerts
      </Title>
      <Paragraph>
        Live manufacturing dashboard alerts and your saved in-app notification inbox (synced from the
        dashboard).
      </Paragraph>

      <Card style={{ marginTop: 16 }} title="Browser push for approvals">
        <BrowserPushManager compact />
      </Card>

      <div style={{ marginTop: 16 }}>
        <Link to="/home/approvals">
          <Button icon={<LinkOutlined />}>Open approval inbox</Button>
        </Link>
      </div>

      <Card style={{ marginTop: 24 }} title="Manufacturing dashboard alerts (live)">
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Data from <Text code>GET /manufacturing/dashboard/alerts</Text>, refreshed about every minute on
          this page.
        </Paragraph>
        {liveError ? (
          <Text type="danger">Could not load live alerts. Try again later.</Text>
        ) : (
          <Spin spinning={liveLoading}>
            {liveList.length === 0 ? (
              <Empty description="No operational alerts right now" />
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={liveList}
                renderItem={(item) => {
                  const tag = severityToTag(item.severity);
                  return (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<LiveAlertIcon severity={item.severity} />}
                        title={
                          <div>
                            {item.title}{' '}
                            <Tag color={tag.color}>{tag.label}</Tag>
                          </div>
                        }
                        description={
                          <div>
                            <div>{item.description}</div>
                            {item.actionRequired ? (
                              <div style={{ marginTop: 4 }}>
                                <Text type="secondary">Action: {item.actionRequired}</Text>
                              </div>
                            ) : null}
                            <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 4 }}>
                              {item.timestamp ? dayjs(item.timestamp).fromNow() : ''}
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </Spin>
        )}
        <Button style={{ marginTop: 8 }} onClick={() => refetchLive()}>
          Refresh live alerts
        </Button>
      </Card>

      <Divider />

      <Card
        style={{ marginTop: 16 }}
        title="In-app inbox (saved)"
        extra={
          <Space>
            <Button
              icon={<SyncOutlined />}
              loading={syncMutation.isPending}
              onClick={() => syncMutation.mutate()}
            >
              Sync from manufacturing dashboard
            </Button>
          </Space>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Checkbox checked={filterUnread} onChange={(e) => setFilterUnread(e.target.checked)}>
            Unread only
          </Checkbox>
          <Select
            style={{ minWidth: 160 }}
            options={MODULE_FILTER_OPTIONS}
            value={filterModule}
            onChange={setFilterModule}
            placeholder="Module"
          />
          <Select
            style={{ minWidth: 180 }}
            options={CATEGORY_FILTER_OPTIONS}
            value={filterCategory}
            onChange={setFilterCategory}
            placeholder="Category"
          />
          <Button
            disabled={selectedIds.length === 0}
            loading={bulkReadMutation.isPending}
            onClick={() => bulkReadMutation.mutate(selectedIds)}
          >
            Mark selected read ({selectedIds.length})
          </Button>
        </Space>
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Persists current dashboard alerts into your notification inbox (deduplicated). Mark items read when
          done. Delivery assignments also appear here for the assigned user. Use filters and open links to jump
          to documents when an action URL is present.
        </Paragraph>
        {inboxError ? (
          <Text type="danger" style={{ display: 'block', marginBottom: 12 }}>
            Could not load inbox: {(inboxErrorObj as Error)?.message || 'request failed'}.{' '}
            <Button type="link" size="small" onClick={() => refetchInbox()}>
              Retry
            </Button>
          </Text>
        ) : null}
        <Spin spinning={inboxLoading || markReadMutation.isPending || bulkReadMutation.isPending}>
          {inboxError ? null : inboxRows.length === 0 ? (
            <Empty
              description={
                <>
                  No inbox notifications yet. Use <strong>Sync from manufacturing dashboard</strong> for
                  operational alerts, or open this page after you are assigned on a delivery note.
                </>
              }
            />
          ) : (
            <>
              {unreadSelectable.length > 0 ? (
                <div style={{ marginBottom: 8 }}>
                  <Checkbox checked={allSelected} indeterminate={selectedIds.length > 0 && !allSelected} onChange={(e) => toggleSelectAll(e.target.checked)}>
                    Select all unread ({unreadSelectable.length})
                  </Checkbox>
                </div>
              ) : null}
              {(() => {
                const byModule = new Map<string, InAppNotificationRow[]>();
                for (const row of inboxRows) {
                  const key = row.module || row.category || 'Other';
                  const list = byModule.get(key) || [];
                  list.push(row);
                  byModule.set(key, list);
                }
                const groups = [...byModule.entries()];
                const renderRow = (item: InAppNotificationRow) => {
                  const eventCode = parseEventCode(item.metadata);
                  return (
                    <List.Item
                      key={item.id}
                      actions={[
                        item.action_url ? (
                          <Button
                            type="link"
                            key="open"
                            icon={<LinkOutlined />}
                            onClick={() => void openNotification(item)}
                          >
                            Open
                          </Button>
                        ) : null,
                        !item.is_read ? (
                          <Button
                            type="link"
                            key="read"
                            icon={<CheckOutlined />}
                            onClick={() => markReadMutation.mutate(item.id)}
                          >
                            Mark read
                          </Button>
                        ) : (
                          <Tag key="read" color="success">
                            Read
                          </Tag>
                        ),
                      ]}
                    >
                      {!item.is_read ? (
                        <Checkbox
                          style={{ marginRight: 12 }}
                          checked={selectedIds.includes(item.id)}
                          onChange={(e) => toggleSelect(item.id, e.target.checked)}
                        />
                      ) : (
                        <span style={{ display: 'inline-block', width: 32, marginRight: 12 }} />
                      )}
                      <List.Item.Meta
                        avatar={<InboxIcon type={item.notification_type} />}
                        title={
                          <div>
                            <span style={{ opacity: item.is_read ? 0.65 : 1 }}>{item.notification_title}</span>{' '}
                            {item.priority ? <Tag>{item.priority}</Tag> : null}
                            {item.module ? <Tag color="blue">{taskModuleLabel(item.module)}</Tag> : null}
                            {item.category ? <Tag color="purple">{item.category}</Tag> : null}
                            {eventCode ? <Tag color="geekblue">{formatEventCode(eventCode)}</Tag> : null}
                          </div>
                        }
                        description={
                          <div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{item.notification_message}</div>
                            <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 4 }}>
                              {item.created_at ? dayjs(item.created_at).fromNow() : ''}
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  );
                };
                if (groups.length <= 1) {
                  return <List itemLayout="horizontal" dataSource={inboxRows} renderItem={renderRow} />;
                }
                return (
                  <Collapse
                    items={groups.map(([label, list]) => ({
                      key: label,
                      label: `${label} (${list.length})`,
                      children: <List itemLayout="horizontal" dataSource={list} renderItem={renderRow} />,
                    }))}
                  />
                );
              })()}
            </>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default MyAlertsPage;
